/**
 * Language identity and rollout state, shared by the editor UI and the
 * execution API.
 *
 * Pure constants only — this module is imported by both the frontend bundle
 * and serverless functions, so it must stay free of db/server imports.
 * Adding a language touches this file plus lib/test-harness/types.ts
 * (SUPPORTED_LANGUAGES / LANGUAGE_IDS); nothing else hand-maintains
 * per-language metadata.
 */

import type { SupportedLanguage } from './test-harness/types.js'

/** Display labels, keyed by editor language value. */
export const LANGUAGE_LABELS = {
  javascript: 'JavaScript',
  python: 'Python',
  cpp: 'C++',
  java: 'Java',
  go: 'Go',
} as const

/** Every language the editor offers (executable or not). */
export type LanguageValue = keyof typeof LANGUAGE_LABELS

/** Dropdown options, in display order. */
export const LANGUAGE_OPTIONS: ReadonlyArray<{
  value: LanguageValue
  label: string
}> = (['javascript', 'python', 'cpp', 'java', 'go'] as const).map((value) => ({
  value,
  label: LANGUAGE_LABELS[value],
}))

/**
 * Languages rolled out to users at all — the per-language kill-switch.
 *
 * This is deliberately NOT "language works on every problem": per-problem
 * availability is derived from artifact presence (starter code, validated
 * templates/custom code — see lib/execution-availability.ts) and a language
 * only reaches users on problems where BOTH gates pass. Removing a language
 * here is the one-line rollback that disables it everywhere at once.
 */
const ROLLED_OUT = [
  'python',
  'javascript',
  'java',
] satisfies SupportedLanguage[]

export const ROLLED_OUT_LANGUAGES: ReadonlySet<string> = new Set(ROLLED_OUT)

/**
 * Type predicate: rolled-out languages are guaranteed members of the
 * execution pipeline's SupportedLanguage set (ROLLED_OUT satisfies
 * SupportedLanguage[] above), so callers can pass a narrowed value to
 * execution APIs without asserting.
 */
export function isLanguageRolledOut<L extends string>(
  language: L,
): language is L & SupportedLanguage {
  return ROLLED_OUT_LANGUAGES.has(language)
}
